/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable no-nested-ternary */
import React, { useState, useCallback, useEffect, ReactNode, FC, MouseEvent, ReactNodeArray } from 'react';
import { useSwipeable } from 'react-swipeable';
import { ControlSize } from './atoms/Controls/Controls';
import { Transform, Wrapper, Slide, CarouselSwipe } from './styled';
import { Controls, Dots } from './atoms';
const controlsPositions = {
  top: 'top',
  bottom: 'bottom'
} as const;
const slideWidthSize = 100; // ширина слайда в процентах

const isValidEvent = (e: KeyboardEvent | MouseEvent) => ['click', 'touchend'].includes(e.type) || e.type === 'keydown' && ['ArrowRight', 'ArrowLeft'].includes((e as KeyboardEvent).key);
export interface CarouselProps {
  autoplay?: boolean;
  delay?: number;
  /**
   * unavailable for mobile
   */
  controlsPosition?: keyof typeof controlsPositions;
  children: ReactNodeArray;
  prevHandler?: (slideId: number, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  nextHandler?: (slideId: number, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  heading?: ReactNode;
  /**
   * translate3d x value
   */
  transformValue?: number;
  /**
   * translate3d x units
   */
  currentSlide?: number;
  transformUnits?: '%' | 'px';
  className?: string;
  withDots?: boolean;
  withControls?: boolean;
  variant?: 'default' | 'premiumBanking';
  /** Возможность перехода на 1 слайд при долистывании до последнего */
  loopSwipe?: boolean;
  controlSize?: ControlSize;
  /** Кастомная настройка положения кнопок от краев слайдера, в px */
  controlsPadding?: number;
}
export const Carousel: FC<CarouselProps> = ({
  transformValue,
  transformUnits,
  heading,
  controlsPosition,
  children,
  autoplay,
  delay,
  prevHandler,
  nextHandler,
  currentSlide = 0,
  className,
  withDots,
  variant,
  withControls,
  loopSwipe,
  controlSize,
  controlsPadding
}) => {
  const [curSlide, setcurSlide] = useState(currentSlide);
  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    setcurSlide(currentSlide);
  }, [currentSlide]);
  useEffect(() => {
    let tid: ReturnType<typeof setTimeout>;
    const play = () => {
      if (!isActive) {
        const slide = curSlide + 1 > children.length - 1 ? 0 : curSlide + 1;
        setcurSlide(slide);
        if (slide > 0 && nextHandler) {
          nextHandler(slide);
        }
        if (slide === 0 && prevHandler) {
          prevHandler(slide);
        }
      }
    };
    if (autoplay) {
      tid = setTimeout(play, delay);
      return () => {
        clearTimeout(tid);
      };
    }
  }, [curSlide, isActive]);
  const handlePrev = useCallback(e => {
    if (!isValidEvent(e)) {
      return;
    }
    const newCurrent = e.type === 'keydown' && e.key === 'ArrowRight' ? Math.min(curSlide + 1, children.length - 1) : Math.max(curSlide - 1, 0);
    setcurSlide(newCurrent);
    if (prevHandler) {
      prevHandler(newCurrent, e);
    }
  }, [curSlide]);
  const handleNext = useCallback(e => {
    if (!isValidEvent(e)) {
      return;
    }
    const newCurrent = e.type === 'keydown' && e.key === 'ArrowLeft' ? Math.max(curSlide - 1, 0) : curSlide === children.length - 1 && loopSwipe ? 0 : Math.min(curSlide + 1, children.length - 1);
    setcurSlide(newCurrent);
    if (nextHandler) {
      nextHandler(newCurrent, e);
    }
  }, [curSlide]);
  const handlers = useSwipeable({
    onSwipedLeft: e => handleNext(e.event),
    onSwipedRight: e => handlePrev(e.event),
    swipeDuration: Infinity,
    preventScrollOnSwipe: true
  });
  const mouseEnterHandler = useCallback(() => setIsActive(true), [isActive]);
  const mouseLeaveHandler = useCallback(() => setIsActive(false), [isActive]);
  return <>
      {children.length > 1 && withControls && <Controls controlsPosition={controlsPosition} controlsPadding={controlsPadding} prevHandler={handlePrev} nextHandler={handleNext} variant={variant} controlSize={controlSize} prevDisabled={curSlide <= 0} nextDisabled={!loopSwipe && curSlide >= children.length - 1} />}

      <CarouselSwipe {...handlers}>
        <Wrapper className={className} tabIndex={0} onKeyDown={handleNext} onMouseEnter={mouseEnterHandler} onMouseLeave={mouseLeaveHandler}>
          {withDots && <Dots currentSlide={curSlide} slidesCount={children.length} />}
          {!!heading && heading}
          <Transform units={transformUnits} value={transformValue == null ? curSlide * slideWidthSize : transformValue}>
            {children.map((item, index) => <Slide key={index}>{item}</Slide>)}
          </Transform>
        </Wrapper>
      </CarouselSwipe>
    </>;
};
Carousel.defaultProps = {
  controlsPosition: controlsPositions.bottom,
  autoplay: true,
  delay: 5000,
  // eslint-disable-next-line no-unused-vars
  prevHandler: () => void 0,
  // need for docs
  // eslint-disable-next-line no-unused-vars
  nextHandler: () => void 0,
  // need for docs,
  transformUnits: '%',
  variant: 'default',
  withControls: true,
  loopSwipe: false
};